import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { IconComponent } from '@mode/capra';
import {
  FileUploadConfig,
  FileUploadEvent,
  FileUploadStatus,
  FileValidationResult,
  UploadedFile,
} from './file-upload.types';

@Component({
  standalone: true,
  selector: 'mode-file-upload',
  imports: [CommonModule, IconComponent],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FileUploadComponent implements OnInit, OnDestroy {
  @Input() config?: FileUploadConfig;

  @Input() acceptedTypes: string[] = [];
  @Input() maxFileSize: number = 25 * 1024 * 1024;
  @Input() maxFileSizeMB?: number;
  @Input() multiple = false;
  @Input() allowDragDrop = true;

  @Input() disabled = false;
  @Input() dragMessage?: string;
  @Input() selectButtonText?: string;
  @Input() showMaxFileSize = false;

  @Input() showAsModal = false;
  @Input() modalDescription = '';
  @Input() showModalActions = true;
  @Input() cancelButtonText = 'Cancel';
  @Input() uploadButtonText = 'Upload';

  @Input() autoUpload = false;
  @Input() uploadUrl?: string;
  @Input() uploadHeaders?: { [key: string]: string };
  @Input() uploadIcon = 'illo-mode-upload-file';

  @Output() filesSelected = new EventEmitter<FileUploadEvent>();
  @Output() fileRemoved = new EventEmitter<FileUploadEvent>();
  //future use cases
  @Output() uploadProgress = new EventEmitter<FileUploadEvent>();
  @Output() uploadComplete = new EventEmitter<FileUploadEvent>();
  @Output() uploadError = new EventEmitter<FileUploadEvent>();
  //--------------------------------

  @Output() uploadRequested = new EventEmitter<File[]>();
  @Output() modalCancel = new EventEmitter<void>();
  @Output() validationErrorsChange = new EventEmitter<string[]>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @HostBinding('class')
  public hostClass = 'mode-file-upload-wrapper';

  public readonly FileUploadStatus = FileUploadStatus;

  public uploadedFiles: UploadedFile[] = [];
  public validationErrors: string[] = [];
  public isDragOver = false;
  public currentlySelectedFile: File | null = null;

  private dragCounter = 0;

  ngOnInit(): void {
    this.setupDragAndDropListeners();
    this.initializeConfig();
  }

  private initializeConfig(): void {
    if (!this.config) {
      this.config = {
        acceptedTypes: this.acceptedTypes,
        maxSize: this.maxFileSizeMB ? this.maxFileSizeMB * 1024 * 1024 : this.maxFileSize,
        multiple: this.multiple,
        dropZone: this.allowDragDrop,
      };
    }
  }

  ngOnDestroy(): void {
    this.removeDragAndDropListeners();
  }

  get hasFiles(): boolean {
    return this.uploadedFiles.length > 0;
  }

  public onModalCancel(): void {
    this.modalCancel.emit();
  }

  public onModalUpload(): void {
    const files = this.uploadedFiles.map((f) => f.file);
    this.uploadRequested.emit(files);
  }

  get defaultDragMessage(): string {
    const fileType = this.getFileTypeHint();
    return this.config?.multiple
      ? `Drag and drop ${fileType} here, or click to select files`
      : `Drag and drop ${fileType} here, or click to select file`;
  }

  get acceptedTypesText(): string {
    if (!this.config || this.config.acceptedTypes.length === 0) return '';

    const types = this.config.acceptedTypes.map((type) => (type.startsWith('.') ? type : `.${type}`)).join(', ');

    const maxSizeMB = (this.config.maxSize / (1024 * 1024)).toFixed(0);
    return `Accepted: ${types} (max ${maxSizeMB}MB)`;
  }

  get acceptedTypesString(): string {
    return this.config?.acceptedTypes.join(',') || '';
  }

  public onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(Array.from(input.files));
      input.value = '';
    }
  }

  public onImportButtonClick(event: Event): void {
    event.stopPropagation();
    this.fileInput.nativeElement.click();
  }

  public onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = false;
    this.dragCounter = 0;

    if (this.disabled) return;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.disabled && !this.isDragOver) {
      this.isDragOver = true;
    }
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragCounter--;
    if (this.dragCounter === 0) {
      this.isDragOver = false;
    }
  }

  public removeFile(fileId: string): void {
    const fileIndex = this.uploadedFiles.findIndex((f) => f.id === fileId);
    if (fileIndex === -1) return;

    const removedFile = this.uploadedFiles[fileIndex];
    this.uploadedFiles.splice(fileIndex, 1);

    if (this.uploadedFiles.length === 0) {
      this.currentlySelectedFile = null;
    }

    this.fileRemoved.emit({
      files: [removedFile],
      type: 'remove',
    });
  }

  public formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private handleFiles(files: File[]): void {
    this.validationErrors = [];

    const newFiles: UploadedFile[] = [];
    let hasValidationErrors = false;

    if (files.length > 0) {
      this.currentlySelectedFile = files[0];
    }

    for (const file of files) {
      const validation = this.validateFile(file);

      if (!validation.isValid) {
        this.validationErrors.push(...validation.errors);
        hasValidationErrors = true;
        continue;
      }

      const existingFile = this.uploadedFiles.find((f) => f.file.name === file.name && f.file.size === file.size);

      if (existingFile) {
        this.validationErrors.push(`File "${file.name}" is already selected`);
        hasValidationErrors = true;
        continue;
      }

      if (!this.config?.multiple && this.uploadedFiles.length > 0) {
        this.uploadedFiles = [];
      }

      const uploadedFile: UploadedFile = {
        file,
        id: this.generateFileId(),
        progress: 0,
        status: FileUploadStatus.Pending,
      };

      newFiles.push(uploadedFile);
      this.uploadedFiles.push(uploadedFile);
    }

    if (hasValidationErrors) {
      this.validationErrorsChange.emit(this.validationErrors);
    }

    // Always emit files selected event (even if empty for invalid files)
    this.filesSelected.emit({
      files: newFiles,
      type: 'select',
    });
  }

  private validateFile(file: File): FileValidationResult {
    const errors: string[] = [];

    if (!this.config) {
      return { isValid: true, errors: [] };
    }

    if (file.size > this.config.maxSize) {
      const maxSizeMB = (this.config.maxSize / (1024 * 1024)).toFixed(0);
      if (this.config.maxSize === 25 * 1024 * 1024) {
        errors.push(`File could not be imported. The file is bigger than the 25 MB size limit.`);
      } else {
        errors.push(`File could not be imported. The file is bigger than the ${maxSizeMB}MB size limit.`);
      }
    }

    if (this.config.acceptedTypes.length > 0) {
      const fileName = file.name.toLowerCase();
      const fileType = file.type.toLowerCase();

      const isValidType = this.config.acceptedTypes.some((acceptedType) => {
        if (acceptedType.includes('*')) {
          const baseType = acceptedType.split('/')[0];
          return fileType.startsWith(baseType + '/');
        }
        if (acceptedType.startsWith('.')) {
          return fileName.endsWith(acceptedType.toLowerCase());
        }
        return fileType === acceptedType.toLowerCase();
      });

      if (!isValidType) {
        errors.push(`File "${file.name}" has an invalid type. Accepted types: ${this.config.acceptedTypes.join(', ')}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private generateFileId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private getFileTypeHint(): string {
    if (!this.config || this.config.acceptedTypes.length === 0) return 'files';

    if (this.config.acceptedTypes.includes('.ipynb')) return 'notebook files';
    if (this.config.acceptedTypes.some((type) => type.includes('image'))) return 'images';
    if (this.config.acceptedTypes.some((type) => ['.csv', '.xlsx', '.xls'].includes(type))) return 'spreadsheet files';
    if (this.config.acceptedTypes.some((type) => ['.pdf'].includes(type))) return 'PDF files';
    if (this.config.acceptedTypes.some((type) => ['.doc', '.docx'].includes(type))) return 'Word documents';

    return 'files';
  }

  private setupDragAndDropListeners(): void {
    document.addEventListener('dragenter', this.preventDefaults);
    document.addEventListener('dragover', this.preventDefaults);
    document.addEventListener('dragleave', this.preventDefaults);
    document.addEventListener('drop', this.preventDefaults);
  }

  private removeDragAndDropListeners(): void {
    document.removeEventListener('dragenter', this.preventDefaults);
    document.removeEventListener('dragover', this.preventDefaults);
    document.removeEventListener('dragleave', this.preventDefaults);
    document.removeEventListener('drop', this.preventDefaults);
  }

  private preventDefaults = (e: DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  };
}
